/**
 * ADU Utah × Build Construx — internal PJM canvas data
 * docs/INTERNAL_ADU_UTAH_PJM.md
 */

export const PJM_META = {
  title: 'ADU Utah × Construx',
  subtitle: 'Internal project canvas',
  northStar:
    'Credible path to a finished ADU at or below $200,000 — proven through a small Utah pilot with clear partner economics.',
  callFocus: 'Align ownership, unknowns, and the smallest pilot that de-risks price + delivery + demand.',
};

export const WORKSTREAMS = [
  {
    id: 'product-economics',
    label: '01',
    title: 'Product and economics',
    objective:
      'Select the first one or two standard models and build an open-book cost model covering frame, materials, labor, sitework, utilities, permitting, finishes, overhead, and partner economics — with a credible path to ≤ $200k finished.',
    knowns: [
      'Construx has a standard panelized / frame product path (R1AS and related catalog models).',
      'Target finished price ceiling: ≤ $200,000 (all-in credibility, not just frame).',
      'Open-book framing is required for nonprofit + partner trust.',
      'Utah ADU policy / nonprofit channel creates demand-side pull (details TBD in pathway workstream).',
    ],
    unknowns: [
      'Which 1–2 SKUs are best for Utah climate, code, and nonprofit program fit?',
      'True all-in cost stack by line item in Utah (sitework + utilities + permit variance by city).',
      'Partner margin / fee structure that still clears ≤ $200k for the customer.',
      'What “finished” means contractually (turnkey vs owner-finish vs hybrid).',
      'Contingency % and who holds risk on site surprises.',
    ],
    subItems: [
      {
        title: 'Model selection',
        items: [
          'Shortlist 1–2 standard models (size, beds/baths, accessibility options)',
          'Confirm Utah energy / snow / seismic envelope implications',
          'Define standard option packages vs change-order only',
        ],
      },
      {
        title: 'Open-book cost model',
        items: [
          'Frame / panel package',
          'Materials (envelope, MEP rough-in allowances)',
          'Labor (factory + field)',
          'Sitework & utilities',
          'Permitting & fees',
          'Finishes (tiers)',
          'Overhead & warranty reserve',
          'Partner economics (fees, GC, nonprofit admin)',
        ],
      },
      {
        title: 'Price path to ≤ $200k',
        items: [
          'Base case, stretch case, and kill criteria',
          'Sensitivity: lot difficulty, city fees, labor rate',
          'What must be standardized vs site-specific allowances',
        ],
      },
    ],
    considerations: [
      'Do not conflate frame price with finished price in external messaging.',
      'Nonprofit funding may restrict eligible costs / profit — model must show allowed categories.',
      'Utah municipal fee variance can break a statewide sticker price; use allowances + city adders.',
    ],
    nextSteps: [
      'Lock SKU shortlist (owners: product + ADU Utah program fit).',
      'Draft v0 open-book spreadsheet with owners per line item.',
      'Identify 3 representative Utah site archetypes for costing.',
      'Schedule cost review with delivery + partner stakeholders.',
    ],
  },
  {
    id: 'nonprofit-pathway',
    label: '02',
    title: 'Nonprofit and customer pathway',
    objective:
      'Confirm program requirements, available funding, applicant status, property and borrower qualification criteria, disbursement process, and how Construx may support or integrate with the existing application flow.',
    knowns: [
      'ADU Utah operates (or is standing up) a nonprofit / program pathway for customers.',
      'Construx is a delivery / product partner, not the funder of record (assumed — confirm).',
      'Customer journey must feel coherent: apply → qualify → price → build → complete.',
    ],
    unknowns: [
      'Exact program eligibility (income, household, veteran, local preference, etc.).',
      'Funding sources, caps, stacking rules, and draw schedule.',
      'Who is the applicant of record vs property owner vs borrower.',
      'Property qualification vs borrower qualification (can they diverge?).',
      'Disbursement triggers tied to milestones / inspections.',
      'Where Construx sits in the app flow (embedded, referred, white-labeled).',
      'Compliance / reporting Construx must support.',
    ],
    subItems: [
      {
        title: 'Program requirements',
        items: [
          'Eligibility checklist (documented source of truth)',
          'Required docs and verification steps',
          'Timeline from apply → funding commitment',
        ],
      },
      {
        title: 'Funding & disbursement',
        items: [
          'Grant vs loan vs blended',
          'Draw schedule vs Construx milestones',
          'Retainage / final payment conditions',
        ],
      },
      {
        title: 'Application integration',
        items: [
          'Map current ADU Utah application steps',
          'Handoff points to Construx intake / pricing',
          'Status sync expectations (CRM fields)',
        ],
      },
    ],
    considerations: [
      'Misaligned draw schedules create cash-flow risk for field partners.',
      'Applicant ≠ owner scenarios need clear legal + funding treatment early.',
      'Avoid duplicate applications — one customer spine across orgs.',
    ],
    nextSteps: [
      'Obtain written program one-pager / policy from ADU Utah.',
      'Walk a sample application end-to-end on a call.',
      'Document disbursement vs construction milestone matrix.',
      'Decide CRM system of record for shared pipeline.',
    ],
  },
  {
    id: 'property-digital',
    label: '03',
    title: 'Property targeting and digital qualification',
    objective:
      'Review ADU Utah’s property-identification and customer-facing systems and determine how they connect with Construx marketing, intake, qualification, pricing, attribution, and partner workflows.',
    knowns: [
      'ADU Utah has (or is building) property-ID and customer-facing digital systems.',
      'Construx needs marketing → intake → qualify → price → partner handoff with attribution.',
      'Site planner / viewer work exists internally but is not a public customer funnel yet.',
    ],
    unknowns: [
      'What data ADU Utah already captures (parcel, zoning, setbacks, utilities, owner).',
      'API / export / webhook capabilities vs manual CSV handoff.',
      'Lead ownership and attribution when both brands generate demand.',
      'How pricing is presented before vs after site qualification.',
      'Partner visibility (what GCs / installers see and when).',
      'Privacy / data-sharing agreements required.',
    ],
    subItems: [
      {
        title: 'Property identification',
        items: [
          'Parcel / address match quality',
          'Feasibility signals (lot size, zoning, ADU allowed)',
          'Exclusion rules (HOA, easements, flood, access)',
        ],
      },
      {
        title: 'Customer-facing systems',
        items: [
          'Current UX for discovery → apply',
          'Self-serve vs counselor-assisted',
          'Content / education touchpoints',
        ],
      },
      {
        title: 'Construx connection map',
        items: [
          'Marketing & campaign attribution',
          'Intake / CRM fields',
          'Qualification gates',
          'Pricing engine / estimate',
          'Partner routing & SLAs',
        ],
      },
    ],
    considerations: [
      'Over-promising from digital property scores without site visit destroys trust.',
      'Attribution fights kill co-marketing — agree rules before launch.',
      'Reuse Construx site-planner tech only where it reduces cycle time, not as vanity 3D.',
    ],
    nextSteps: [
      'Demo ADU Utah property + customer tools (screen share).',
      'Produce integration options: A) deep link, B) API, C) shared CRM.',
      'Define minimum viable lead payload between systems.',
      'Draft data-sharing / brand guidelines one-pager.',
    ],
  },
  {
    id: 'delivery-fulfillment',
    label: '04',
    title: 'Delivery and fulfillment model',
    objective:
      'Define who owns each stage from lead generation through feasibility, design, permitting, procurement, frame delivery, field execution, inspection, and customer completion. Validate the labor model and the 60–100-day delivery target.',
    knowns: [
      'Construx strength: digital manufacturing / frame package.',
      'Field execution typically needs local partner capacity.',
      'Stated delivery ambition: 60–100 days (validate definition of start/end).',
    ],
    unknowns: [
      'RACI for every stage (ADU Utah / Construx / GC / specialty trades / AHJ).',
      'Labor model: W2 crews, preferred GC network, or hybrid?',
      'Permitting ownership and typical Utah city cycle times.',
      'Inspection sequencing and failure / rework loops.',
      'What “day 0” and “day complete” mean (contract, permit, start of frame, CO).',
      'Capacity constraints for first 10 / 50 / 200 units.',
    ],
    subItems: [
      {
        title: 'Stage ownership (draft RACI)',
        items: [
          'Lead gen & marketing',
          'Feasibility / site check',
          'Design / plot plan',
          'Permitting',
          'Procurement',
          'Frame delivery',
          'Field execution & finishes',
          'Inspections',
          'Customer completion / warranty handoff',
        ],
      },
      {
        title: 'Labor model',
        items: [
          'Crew composition by phase',
          'Training / certification needs',
          'Backup capacity if a partner drops',
        ],
      },
      {
        title: '60–100 day target',
        items: [
          'Critical path diagram',
          'Permit float assumptions',
          'Weather / winter schedule impact',
          'Customer decision latency buffers',
        ],
      },
    ],
    considerations: [
      'If permitting is on the critical path, 60 days may be infeasible in slow AHJs without pre-approved plans.',
      'Frame-fast does not equal finish-fast — finishes and utility hooks dominate variance.',
      'Clear single throat to choke for customer communication.',
    ],
    nextSteps: [
      'Fill RACI live on the call (names, not just orgs).',
      'Define start/end clocks for the 60–100 day KPI.',
      'List top 5 schedule killers and mitigations.',
      'Confirm pilot GC / installer shortlist in Utah.',
    ],
  },
  {
    id: 'pilot-scale',
    label: '05',
    title: 'Pilot and scale plan',
    objective:
      'Identify the smallest pilot cohort that can prove pricing, delivery, customer demand, and partner economics — then the path from those projects to the broader nonprofit opportunity and Utah market.',
    knowns: [
      'Pilot must prove four things: price, delivery, demand, partner economics.',
      'Scale story ties to nonprofit volume + Utah market expansion.',
      'Smallest credible cohort beats a large vague launch.',
    ],
    unknowns: [
      'Pilot N (units / households) and geography (cities).',
      'Success metrics and kill / expand thresholds.',
      'Funding for pilot vs production (who capitalizes early units).',
      'How learnings feed SKU / cost / process updates.',
      'Timeline to “broader nonprofit opportunity” decision gate.',
    ],
    subItems: [
      {
        title: 'Minimum pilot design',
        items: [
          'Proposed N (e.g. 3–8 units) and why that proves the four pillars',
          'Mix of site archetypes',
          'Customer segments (program-funded vs cash / hybrid)',
        ],
      },
      {
        title: 'Proof criteria',
        items: [
          'Price: all-in ≤ $200k (or documented exception policy)',
          'Delivery: hit agreed day-clock on ≥ X% of units',
          'Demand: qualified pipeline conversion rates',
          'Partner economics: sustainable margins for each party',
        ],
      },
      {
        title: 'Scale path',
        items: [
          'Pilot → program expansion gates',
          'Utah market sequencing (city by city)',
          'Ops / supply / partner capacity ramp',
        ],
      },
    ],
    considerations: [
      'Pilot that only proves frame cost does not unlock nonprofit scale.',
      'Over-customizing pilot units destroys learning for the standard SKU.',
      'Publish a shared scorecard before first pour / set.',
    ],
    nextSteps: [
      'Agree pilot N, cities, and success scorecard on this call.',
      'Name executive sponsors on both sides.',
      'Set 2-week follow-ups: cost model v0 + RACI v0 + systems map v0.',
      'Book pilot kickoff only after scorecard + SKU lock.',
    ],
  },
];
